import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  FC,
  RefObject,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { Editor as TinyMCEEditorInstance } from 'tinymce';

import { getSessionMappings, fetchSourceFile } from '../services/apiCalls';
import { UserInfoContext } from '../pages/Container';
import {
  IMapComment,
  IMappingContext,
  EditorRefObject,
  MutableEditorRef,
  TargetMapping,
} from '../types';

export const defaultMappingContext: IMappingContext = {
  sourceFileDoc: {},
  setSourceFileDoc: () => undefined,
  currentMapping: undefined,
  setCurrentMapping: () => undefined,
  finishedScoring: false,
  setFinishedScoring: () => undefined,
  sessionMappings: [],
  setSessionMappings: () => undefined,
  mappingSession: undefined,
  setMappingSession: () => undefined,
  showLoader: false,
  setShowLoader: () => undefined,
  editor1Content: { html: '' },
  setEditor1Content: () => undefined,
  mappingComments: [],
  setMappingComments: () => undefined,
  isCommentDisplayed: false,
  commentNumber: 1,
  setCommentNumber: () => undefined,
  editor1Ref: { current: null } as MutableEditorRef,
  editor2Ref: { current: null } as MutableEditorRef,
  setDocStates: () => undefined,
  markMappingDone: () => undefined,
  fetchTargetFile: () => undefined,
  resetStates: () => undefined,
  toggleComments: () => undefined,
  handleSuperscript: () => undefined,
  isPreviousAvailable: () => false,
  isNextAvailable: () => false,
  handleNavigate: (_type: string) => undefined,
  setIsCommentDisplayed: () => undefined,
  targetId: 0,
  setTargetId: () => undefined,
  getElements: () => undefined,
  addCommentBubble: () => undefined,
};

export const MappingContext = createContext<IMappingContext>(defaultMappingContext);

interface Props {
  children: ReactNode;
}

const escapeNodeIdForSelector = (nodeId: string) =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(nodeId)
    : nodeId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

/** TinyMCE target editor exposes getDoc(); legacy code wrongly treated editor1Ref as an iframe. */
const getTargetEditorDocument = (ref: RefObject<unknown>): Document | null => {
  const ed = ref?.current as { getDoc?: () => Document } | null | undefined;
  return ed?.getDoc?.() ?? null;
};

export const MappingContextContainer: FC<Props> = ({ children }) => {
  const { firstName, lastName } = useContext(UserInfoContext);

  const location = useLocation();

  const [sourceFileDoc, setSourceFileDoc] = useState(defaultMappingContext.sourceFileDoc);
  const [currentMapping, setCurrentMapping] = useState<TargetMapping | undefined>(
    defaultMappingContext.currentMapping,
  );
  const [finishedScoring, setFinishedScoring] = useState(defaultMappingContext.finishedScoring);
  const [sessionMappings, setSessionMappings] = useState<TargetMapping[] | undefined>(
    defaultMappingContext.sessionMappings,
  );
  const [mappingSession, setMappingSession] = useState(defaultMappingContext.mappingSession);
  const [showLoader, setShowLoader] = useState(defaultMappingContext.showLoader);
  const [editor1Content, setEditor1Content] = useState(defaultMappingContext.editor1Content);
  const [mappingComments, setMappingComments] = useState<IMapComment[]>(
    defaultMappingContext.mappingComments,
  );
  const [isCommentDisplayed, setIsCommentDisplayed] = useState(
    defaultMappingContext.isCommentDisplayed,
  );
  const [commentNumber, setCommentNumber] = useState(defaultMappingContext.commentNumber);
  const [targetId, setTargetId] = useState(defaultMappingContext.targetId);
  const editor1Ref = useRef<TinyMCEEditorInstance | null>(null);
  const editor2Ref = useRef<TinyMCEEditorInstance | null>(null);

  const markMappingDone = (key: string, value = 'done') => {
    setSessionMappings((prevSessionMappings) =>
      prevSessionMappings?.map((mapping) => {
        if (mapping.mappingId === currentMapping?.mappingId) {
          const updateMapping = { ...mapping };
          if (key === 'status') {
            updateMapping['statusupdatername'] = `${firstName} ${lastName}`;
          }
          return { ...updateMapping, [key]: value };
        } else {
          return mapping;
        }
      }),
    );
  };

  const handleNavigate = (direction: string) => {
    const index = (currentMapping?.mappingNumber || 0) - 1;
    return sessionMappings && sessionMappings[direction === 'next' ? index + 1 : index - 1];
  };

  const getElements = (editor: EditorRefObject, query: string) =>
    editor?.current?.getDoc()?.querySelectorAll(query);

  const setDocStates = () => {
    getSessionMappings(location.state?.id)
      .then(async (res) => {
        setSessionMappings(res.data.mappings?.map((o: TargetMapping) => ({ ...o })));
        setTargetId(res.data.catalogId);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const isPreviousAvailable = () => {
    return currentMapping?.mappingNumber === 1 ? false : true;
  };

  const isNextAvailable = () => {
    return currentMapping?.mappingNumber === sessionMappings?.length ? false : true;
  };

  const resetStates = () => {
    setSessionMappings(undefined);
    setCurrentMapping(undefined);
    setSourceFileDoc({});
    setEditor1Content({ html: '' });
    setMappingSession(undefined);
    setShowLoader(false);
  };

  const fetchTargetFile = async () => {
    const res = await fetchSourceFile(location.state.doc.key || location.state.id);
    setEditor1Content({ html: res.data.html });
  };

  const toggleComments = async () => {
    setIsCommentDisplayed((previous) => !previous);
  };

  const handleSuperscript = (
    name = firstName,
    number = commentNumber,
    mappingNumber = currentMapping?.mappingNumber,
    targetNodeId = currentMapping?.mappingId,
  ) => {
    const doc = getTargetEditorDocument(editor1Ref);
    if (!doc || !targetNodeId) {
      return;
    }
    const query = `[mapping="${mappingNumber}"], [data-nodeid="${escapeNodeIdForSelector(
      String(targetNodeId),
    )}"]`;
    const dataPoints = doc.querySelectorAll(query) as NodeListOf<HTMLElement>;
    Array.from(dataPoints).forEach((el) => {
      if (isCommentDisplayed) {
        const sup = document.createElement('sup');
        sup.textContent = `${name.slice(0, 1)}${number}`;
        el.appendChild(sup);
      } else {
        const sup = el.querySelector('sup');
        sup && el.removeChild(sup);
      }
    });
  };

  const addCommentBubble = (
    mappingNumber = currentMapping?.mappingNumber,
    targetNodeId = currentMapping?.mappingId,
  ) => {
    const doc = getTargetEditorDocument(editor1Ref);
    if (!doc || !targetNodeId) {
      return;
    }
    const escaped = escapeNodeIdForSelector(String(targetNodeId));
    const candidates: HTMLElement[] = [
      ...Array.from(doc.querySelectorAll(`[data-nodeid="${escaped}"]`) as NodeListOf<HTMLElement>),
    ];
    if (mappingNumber != null && String(mappingNumber) !== '') {
      candidates.push(
        ...Array.from(
          doc.querySelectorAll(
            `[mapping="${escapeNodeIdForSelector(String(mappingNumber))}"]`,
          ) as NodeListOf<HTMLElement>,
        ),
      );
    }
    const seen = new Set<HTMLElement>();
    const dataPoints = candidates.filter((el) => {
      if (seen.has(el)) return false;
      seen.add(el);
      return true;
    });

    dataPoints.forEach((el) => {
      if (el.querySelector("[data-comment='true']")) {
        return;
      }
      const dot = document.createElement('div');
      dot.setAttribute('contenteditable', 'false');
      dot.setAttribute('data-comment', 'true');
      dot.setAttribute('title', 'View comments');
      dot.style.cssText =
        'cursor:pointer;background-color:deepskyblue;width:10px;height:10px;border-radius:50%;position:absolute;top:5px;right:5px;z-index:2;';
      el.style.position = 'relative';
      el.appendChild(dot);
    });
  };

  useEffect(() => {
    setIsCommentDisplayed(false);
  }, [editor1Content.html]);

  return (
    <MappingContext.Provider
      value={{
        setDocStates,
        markMappingDone,
        sourceFileDoc,
        setSourceFileDoc,
        mappingSession,
        setMappingSession,
        currentMapping,
        setCurrentMapping,
        finishedScoring,
        setFinishedScoring,
        sessionMappings,
        setSessionMappings,
        showLoader,
        setShowLoader,
        editor1Content,
        setEditor1Content,
        fetchTargetFile,
        resetStates,
        mappingComments,
        setMappingComments,
        toggleComments,
        isCommentDisplayed,
        setIsCommentDisplayed,
        editor1Ref,
        editor2Ref,
        commentNumber,
        setCommentNumber,
        handleSuperscript,
        isPreviousAvailable,
        isNextAvailable,
        handleNavigate,
        targetId,
        setTargetId,
        getElements,
        addCommentBubble,
      }}>
      {children}
    </MappingContext.Provider>
  );
};

export default MappingContextContainer;
